import {
    Box,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Stack,
    useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import Dropdown from '../dropdown';
import Button from '../button';
import InputField from '../formItem/inputField';
import CountAdjuster from '../countAdjuster';
import { ORGANIZATION_TYPES } from '../../constants';
import { useOrganizationTypeEdit } from '../../hooks/useOrganizationTypeEdit';

const OrganizationTypeEditDialog = ({
    open,
    onClose,
    onSave,
    initialType = '',
    initialDivisionSections = null,
    disabled = false
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const {
        organizationType,
        selectedGrades,
        gradeNames,
        gradeClasses,
        handleOrganizationTypeChange,
        adjustGradeCount,
        handleGradeNameChange,
        adjustClassCount,
        handleClassNameChange,
        getDivisionSections,
        isValid
    } = useOrganizationTypeEdit(initialType, initialDivisionSections);

    const handleSave = () => {
        const divisionSections = getDivisionSections();
        if (onSave) {
            onSave({
                type: organizationType,
                divisionSections
            });
        }
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            slotProps={{
                root: { keepMounted: false },
            }}
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    backgroundColor: theme.palette.background.default,
                    border: `1px solid ${theme.palette.figma.white[20]}`,
                }
            }}
        >
            <DialogTitle sx={{
                textAlign: 'center',
                pb: 1,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                    <Typography variant="h5" sx={{
                        fontWeight: 'bold',
                        color: theme.palette.figma.white[95]
                    }}>
                        {t('please_enter_organization_type_and_division_details')}
                    </Typography>
                </Box>
                <Typography variant="body2" sx={{
                    color: theme.palette.figma.white[70],
                    fontSize: '14px'
                }}>
                    {t('for_grade_and_class_rankings_and_statistics')}
                </Typography>
            </DialogTitle>

            <DialogContent sx={{
                p: 3,
                maxHeight: '70vh',
                overflowY: 'auto'
            }}>
                {/* 기관유형 */}
                <Box sx={{ mb: 8 }}>
                    <Typography sx={{
                        mb: 2,
                        fontWeight: 500,
                        fontSize: '14px',
                        letterSpacing: '-0.42px',
                        textAlign: 'left'
                    }}>
                        {t('organization_type')}
                    </Typography>
                    <Dropdown
                        options={ORGANIZATION_TYPES}
                        value={organizationType}
                        onChange={handleOrganizationTypeChange}
                        placeholder={t('please_select')}
                        disabled={disabled}
                    />
                </Box>

                {/* 세부소속1 - 학년 설정 */}
                {organizationType !== '' && organizationType !== null && organizationType !== undefined && (
                    <Box sx={{ mb: 8 }}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 2
                        }}>
                            <Typography sx={{
                                fontWeight: 500,
                                fontSize: '14px',
                                letterSpacing: '-0.42px',
                                textAlign: 'left'
                            }}>
                                {t('division_1')}
                            </Typography>

                            {/* 학년 개수 조정 */}
                            <CountAdjuster
                                grade="grade"
                                count={selectedGrades.length}
                                onAdjust={(grade, delta) => adjustGradeCount(delta)}
                            />
                        </Box>

                        {/* 학년 이름 입력 */}
                        {selectedGrades.length > 0 && (
                            <Stack spacing={1}>
                                {selectedGrades.map((gradeNumber, index) => {
                                    const orgType = ORGANIZATION_TYPES.find(type => type.value === organizationType);
                                    const isSingleGrade = orgType && orgType.grades && orgType.grades.length === 1;
                                    const placeholder = isSingleGrade
                                        ? t('class_name_e_g_grade', { grade: t(orgType.grades[0]) })
                                        : '';

                                    return (
                                        <InputField
                                            key={`grade-${gradeNumber}-${index}`}
                                            placeholder={placeholder}
                                            value={gradeNames[gradeNumber] || ''}
                                            onChange={(e) => handleGradeNameChange(gradeNumber, e.target.value)}
                                            size="small"
                                        />
                                    );
                                })}
                            </Stack>
                        )}
                    </Box>
                )}

                {/* 세부소속2 - 반 설정 */}
                {organizationType !== '' && organizationType !== null && organizationType !== undefined && (() => {
                    const orgType = ORGANIZATION_TYPES.find(type => type.value === organizationType);
                    return orgType && orgType.grades && orgType.grades.length > 1;
                })() && (
                        <Box>
                            <Typography sx={{
                                mb: 2,
                                fontWeight: 500,
                                fontSize: '14px',
                                letterSpacing: '-0.42px',
                                textAlign: 'left'
                            }}>
                                {t('division_2')}
                            </Typography>
                            <Grid container spacing={2}>
                                {selectedGrades.map(grade => (
                                    <Grid item xs={12} sm={6} md={4} key={grade}>
                                        <Box sx={{
                                            p: 2,
                                            border: `1px solid ${theme.palette.figma.white[20]}`,
                                            borderRadius: '8px',
                                            backgroundColor: theme.palette.figma.white[5]
                                        }}>
                                            <Typography sx={{
                                                mb: 2,
                                                fontWeight: 500,
                                                textAlign: 'center',
                                                color: theme.palette.text?.primary || 'rgba(255, 255, 255, 0.9)',
                                            }}>
                                                {gradeNames[grade] ? t(gradeNames[grade]) : ''}
                                            </Typography>

                                            {/* 반 개수 조정 */}
                                            <CountAdjuster
                                                grade={grade}
                                                count={gradeClasses[grade]?.count || 0}
                                                onAdjust={adjustClassCount}
                                            />

                                            {/* 반 이름 입력 */}
                                            <Stack spacing={1}>
                                                {(gradeClasses[grade]?.classes || []).map((className, index) => {
                                                    const orgType = ORGANIZATION_TYPES.find(type => type.value === organizationType);
                                                    const baseClass = orgType?.class || 'class_1';
                                                    return (
                                                        <InputField
                                                            key={`class-${grade}-${index}`}
                                                            placeholder={t('class_name_e_g_example', { example: t(baseClass) })}
                                                            value={className}
                                                            onChange={(e) => handleClassNameChange(grade, index, e.target.value)}
                                                            size="small"
                                                        />
                                                    );
                                                })}
                                            </Stack>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    )}

                {organizationType !== '' && organizationType !== null && organizationType !== undefined && selectedGrades.length > 0 && (
                    <Typography sx={{
                        mt: 3,
                        fontSize: '12px',
                        color: theme.palette.figma.white[60],
                        textAlign: 'center'
                    }}>
                        {t('use_to_set_the_number_of_classes_per_grade_and_enter_class_names')}
                    </Typography>
                )}

                {organizationType !== '' && organizationType !== null && organizationType !== undefined && selectedGrades.length === 0 && (
                    <Typography sx={{
                        mt: 3,
                        fontSize: '12px',
                        color: theme.palette.figma.white[60],
                        textAlign: 'center'
                    }}>
                        {t('click_the_plus_button_in_division_1_to_add_grades')}
                    </Typography>
                )}
            </DialogContent>

            <DialogActions sx={{
                p: 3,
                gap: 2,
                justifyContent: 'center'
            }}>
                <Button
                    variant="gray"
                    onClick={onClose}
                    sx={{ minWidth: '120px' }}
                >
                    {t('cancel')}
                </Button>
                <Button
                    variant="normal"
                    onClick={handleSave}
                    disabled={!isValid()}
                    sx={{ minWidth: '120px' }}
                >
                    {t('save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default OrganizationTypeEditDialog;

